import React, { memo } from 'react';
import { isFedrampModerate, isIsolatedCloud } from '@atlaskit/atlassian-context';
import { MenuSection, MenuSectionHeading, MenuList } from '@atlaskit/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

import type { Features } from '../../common/types.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';
import messages from './messages.tsx';

type Props = {
	features: Features;
};

const IntegrationsSection = ({ features }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { atlassianAccountId } = useTenantContext();
	const currentRoute = useCurrentRoute();

	return (
		<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
			<MenuSectionHeading>{formatMessage(messages.heading)}</MenuSectionHeading>
			<MenuList>
				{features.hasApplicationLinksIntegration && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'applicationLinks' }}
						isSelected={currentRoute.name === 'global-settings-products-applinks'}
						href="/plugins/servlet/applinks/listApplicationLinks"
					>
						{formatMessage(messages.applicationLinksItem)}
					</LinkMenuItem>
				)}
				{features.hasCompassPageIntegration &&
					!isFedrampModerate() &&
					!(isIsolatedCloud() && fg('hide_isolated_cloud_compass_component_in_jira')) && (
						<LinkMenuItem
							analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'compass' }}
							isSelected={currentRoute.name === 'compass-product-settings'}
							href="/jira/settings/products/compass"
						>
							{formatMessage(messages.compassItem)}
						</LinkMenuItem>
					)}
				{features.hasDvcsAccountsIntegration && !isDevopsFeatureDisabledInFedRamp() && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'dvcsAccounts' }}
						isSelected={currentRoute.name === 'global-settings-products-dvcs'}
						href="/secure/admin/ConfigureDvcsOrganizations!default.jspa"
					>
						{formatMessage(messages.dvcsAccountsItem)}
					</LinkMenuItem>
				)}
				{features.hasStatusPageIntegration && !!atlassianAccountId && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'statuspage' }}
						isSelected={currentRoute.name === 'global-settings-products-jsd-status'}
						href={`/plugins/servlet/ac/jsd-status-page/admin-page?user.key=${atlassianAccountId}&user.id=${atlassianAccountId}`}
					>
						{formatMessage(messages.statuspageItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

export default memo(IntegrationsSection);
